import React, { useEffect, useRef, useState } from "react";
import {
  useLargeBuyTrades,
  useLargeSellTrades,
  useTopTraders,
} from "../../../hooks"; // Adjust the import path
import {
  Pagination,
  Skeleton,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Divider,
  Stack,
  Collapse,
  Button,
  useTheme,
} from "@mui/material";
import moment from "moment/moment";
import 'moment/locale/zh-cn'; // Import Chinese locale
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useBitcoinPrice } from '../../../state/context/bitcoin.context'; // Adjust path if needed
import QuickBuy from "../../Common/QuickBuy.component";

function transformToBTC(value) {
  const sats = value / 1000;
  const btc = sats / 100000000;
  return btc;
}

function formatNumber(n) {
  // Divide by 1e11
  n = n / 1e11;

  // Determine the appropriate suffix
  if (n >= 1e9) {
    return (n / 1e9).toFixed(2) + "B";
  } else if (n >= 1e6) {
    return (n / 1e6).toFixed(2) + "M";
  } else if (n >= 1e3) {
    return (n / 1e3).toFixed(2) + "K";
  } else {
    return n.toFixed(2);
  }
}

const CollapsibleTableRow = ({ trader }) => {
  const { price: btcPrice } = useBitcoinPrice(); // <<< Get BTC price from context
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Set moment locale based on current language
  React.useEffect(() => {
    moment.locale(i18n.language === 'zh' ? 'zh-cn' : 'en');
  }, [i18n.language]);

  // Get locale for number formatting
  const locale = i18n.language === 'zh' ? 'zh-CN' : 'en-US';

  const handleMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timerRef.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  // --- Data processing ---
  const tokenIdForDisplay = trader.tokenid; // Verify correct ID property
  const tokenIdForLink = trader.token?.id;  // Verify correct ID property
  const tokenName = trader.token?.name;

  // Calculate BTC amount numerically first
  const btcAmount = transformToBTC(trader.amount_btc);
  // Calculate USD amount
  const amountUSD = btcPrice ? btcAmount * btcPrice : null;

  return (
    <TableRow
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        cursor: "pointer",
        position: 'relative', // Add relative positioning to the row
        backgroundColor: open ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[100]) : 'inherit',
        transition: "background-color 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.1) !important",
        },
      }}
    >
      {/* Trader Cell */}
      <TableCell sx={{ py: 0.5, px: 1 }}>
        <Button
          variant="text"
          size="small"
          sx={{
            fontSize: '0.65rem',
            fontWeight: 'bold',
            minWidth: 'auto',
            padding: '2px 4px',
            color: 'primary.main',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
          }}
          onClick={() => window.open(`/users/${ trader.user }`)}
        >
          {trader.user.slice(0, 3)}...{trader.user.slice(-3)}
        </Button>
      </TableCell>

      {/* Token Cell */}
      <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
        <Button
          variant="text"
          size="small"
          sx={{
            fontSize: '0.65rem',
            fontWeight: 'bold',
            minWidth: 'auto',
            padding: '2px 4px',
            color: 'primary.main',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' }
          }}
          onClick={() => tokenIdForLink && window.open(`/tokens/${ tokenIdForLink }`)}
          disabled={!tokenIdForLink}
        >
          <Avatar
            alt={tokenName || ''}
            src={`https://images.odin.fun/token/${ tokenIdForDisplay }`}
            sx={{ width: 14, height: 14, mr: 0.25 }}
          />
          <Typography variant="caption" component="span" sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}>
            {(tokenName || 'N/A').length > 6 ? (tokenName || 'N/A').slice(0, 6) + '...' : (tokenName || 'N/A')}
          </Typography>
        </Button>
      </TableCell>

      {/* Amount (Token) Cell */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.65rem', fontWeight: 'bold' }}>
        {formatNumber(trader.amount_token)}
      </TableCell>

      {/* Amount (BTC/USD) Cell - UPDATED */}
      <TableCell align="right" sx={{ py: 0.5, px: 1 }}>
        <Stack direction="column" spacing={0.25} alignItems="flex-end">
          {/* Display formatted BTC amount */}
          <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1, fontWeight: 'bold' }}>
            {btcAmount.toFixed(3)} BTC
          </Typography>
          {/* Display formatted USD amount if available */}
          {amountUSD !== null && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: '0.6rem', lineHeight: 1, fontWeight: 'bold' }}>
              ${(amountUSD / 1000).toFixed(1)}K
            </Typography>
          )}
        </Stack>
      </TableCell>

      {/* Time Cell */}
      <TableCell align="right" sx={{ py: 0.5, px: 1, fontSize: '0.65rem', fontWeight: 'bold' }}>
        {moment(trader.time).fromNow().replace(' ago', '').replace('a few seconds', '1s').replace('a minute', '1m').replace('an hour', '1h').replace('a day', '1d')}
      </TableCell>

      {/* Collapsible Content - Positioned absolutely to span full row width */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 1,
            p: 2,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '60px',
          }}
        >
          {tokenIdForDisplay ? (
            <QuickBuy tokenId={tokenIdForDisplay} />
          ) : (
            <Typography variant="caption" color="error">{t('messages.cannotLoadQuickBuy')}</Typography>
          )}
        </Box>
      </Collapse>
    </TableRow>
  );
};

const BuyTrades = ({ selectedTimeOffset }) => {
  const { t } = useTranslation();
  const { data, loading, error, page, setPage, hasNextPage } =
    useLargeBuyTrades(selectedTimeOffset, 10);

  if (loading && !data.length)
    return (
      <Skeleton
        sx={{ flexGrow: 1 }}
        variant="rectangular"
        // width={210}
        // height={118}
        style={{ width: "100%", height: 300 }}
      />
    );
  if (error)
    return (
      <Typography color="error" align="center">
        {t('common.error')}: {error?.message}
      </Typography>
    );

  return (
    <Box sx={{ width: "100%", mx: "auto", overflow: "hidden", flexGrow: 1, display: "flex", flexDirection: "column" }}>
      {data.length === 0 ? (
        <Typography align="center">{t('common.noDataAvailable')}</Typography>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(odd)": {
            //   backgroundColor: "rgba(255, 255, 255, 0.02)",
            // },
            // "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            //   backgroundColor: "rgba(255, 255, 255, 0.05)",
            // },
            // "& .MuiTableBody-root .MuiTableRow-root:hover": {
            //   backgroundColor: "rgba(255, 255, 255, 0.1) !important",
            // },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }}>
                  <strong>{t('table.trader')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.token')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.amountToken')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.amountBtc')}</strong>
                </TableCell>
                <TableCell sx={{ backgroundColor: "black", py: 0.5, px: 1, fontSize: '0.7rem', minWidth: 'auto' }} align="right">
                  <strong>{t('table.time')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((trader) => (
                <CollapsibleTableRow key={trader._id} trader={trader} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination Controls */}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={hasNextPage ? page + 1 : page} // Assume one extra page exists
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size="small"
        />
      </Box>
    </Box>
  );
};

export default BuyTrades;
